import store from '@/state/store';
import { EventScope } from '@bimdata/definitions/const';
import commonSlice from '@/state/common.slice';
import { handleError } from '@/actions/common';
import { apply as applyProjectEvent, getNotification } from './projects';
import { EventQueue as ModelEventQueue } from './models';
import { Event, ModelEvent } from '@bimdata/definitions/dto';
import { JWT } from '@bimdata/client/contracts/jwt';
import api from '@/api/api';

const worker = new Worker(new URL('./events-watcher-worker', import.meta.url), {
  type: 'module',
});

const onSessionOpened = (jwt: JWT) => {
  worker.postMessage({
    type: 'sessionOpened',
    jwt
  });
}

const onSessionClosed = (jwt: JWT) => {
  worker.postMessage({
    type: 'sessionClosed',
    jwt,
  });
}

api.addEventListener('sessionOpened', onSessionOpened);
api.addEventListener('sessionClosed', onSessionClosed);

type Message = { type: 'sessionExpired', jwt: JWT } | { type: 'applyEvents', onlyNotify: boolean, events: Event[] };

worker.onmessage = async (message: MessageEvent<Message>) => {
  switch (message.data.type) {
    case 'sessionExpired': {
      const jwt = api.getSession();
      if (!jwt || (message.data.jwt?.access === jwt.access)) {
        api.refreshToken();
      } else {
        onSessionOpened(jwt);
      }
    }
    break;
    case 'applyEvents':
      await applyEvents(message.data.onlyNotify, message.data.events);
      break;
  }
};

const applyEvents = async (onlyNotify: boolean, events: Event[]) => {
  const notifications = [];

  let meq: ModelEventQueue | null = null;
  for (let event of events) {
    try {
      if (!onlyNotify) {
        switch (event.scope) {
          case EventScope.MODEL: {
            if (!meq) meq = new ModelEventQueue();
            meq.enqueue(event as ModelEvent);
          }
            break;
          case EventScope.PROJECT:
            await applyProjectEvent(event);
            break;
        }
      }

      if (event.scope === EventScope.PROJECT) {
        const n = await getNotification(event);
        if (n) {
          notifications.push(n);
        }
      }
    } catch (error) {
      handleError(error);
    }
  }

  if (meq) {
    await meq.apply();
  }

  if (notifications.length) {
    store.dispatch(commonSlice.actions.addNotifications(notifications));
  }
}

const startFetchCycle = async () => {
  worker.postMessage({
    type: 'start',
    paused: document.hidden,
    delayed: !document.hasFocus(),
    session: api.getSession()
  });

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("blur", slowDown);
  window.addEventListener("focus", resume);
};

const resume = () => {
  worker.postMessage({
    type: 'resume',
    delayed: !document.hasFocus()
  });
}

const pause = () => {
  worker.postMessage({ type: 'pause' });
}

api.addEventListener('sessionClosed', pause);

const slowDown = () => {
  worker.postMessage({ type: 'slowDown' });
}

const onVisibilityChange = () => {
  if (!document.hidden) {
    resume();
  } else {
    pause();
  }
}

const stopFetchCycle = async () => {
  window.removeEventListener("focus", resume);
  window.removeEventListener("blur", slowDown);
  document.removeEventListener("visibilitychange", onVisibilityChange);
  pause();
};

export {
  startFetchCycle,
  stopFetchCycle,
};
